import * as crypto from 'crypto';

export function encryptString(text: string): string {
    const key = process.env.REACT_APP_ENCRYPTION_KEY || 'key';
    // Generate random IV (16 bytes)
    const iv = crypto.randomBytes(16);
    
    // Pad key to 32 bytes (UTF-8)
    const keyBytes = Buffer.from(key.padEnd(32).substring(0, 32), 'utf-8');
    
    // Encrypt
    const cipher = crypto.createCipheriv('aes-256-cbc', keyBytes, iv);
    let encrypted = cipher.update(text, 'utf-8', 'binary');
    encrypted += cipher.final('binary');
    
    // Combine IV + ciphertext and return as Base64
    const combined = Buffer.concat([iv, Buffer.from(encrypted, 'binary')]);
    return combined.toString('base64');
}

export function decryptString(encryptedText: string): string {
   if (!encryptedText) {
       return "";
   }

    try {
         const key = process.env.REACT_APP_ENCRYPTION_KEY || 'key';
        // 1. Decode Base64
        const encryptedBuffer = Buffer.from(encryptedText, 'base64');

        // 2. Extract IV (first 16 bytes)
        const iv = encryptedBuffer.subarray(0, 16);
        const ciphertext = encryptedBuffer.subarray(16);

        // 3. Prepare the key (same as C#)
        const keyBytes = Buffer.from(key.padEnd(32).substring(0, 32), 'utf-8');

        // 4. Decrypt
        const decipher = crypto.createDecipheriv('aes-256-cbc', keyBytes, iv);
        decipher.setAutoPadding(true); // PKCS7 padding (default in Node.js)

        let decrypted = decipher.update(ciphertext);
        decrypted = Buffer.concat([decrypted, decipher.final()]);

        return decrypted.toString('utf-8');
    } catch (error) {
        console.error("Decryption failed:", error);
        throw new Error("Invalid key or corrupted data");
    }
}

export function encryptBytes(data: Buffer): string {
     const key = process.env.REACT_APP_ENCRYPTION_KEY || 'key';
    // Generate random IV (16 bytes)
    const iv = crypto.randomBytes(16);
    
    // Prepare key (UTF-8, padded to 32 bytes)
    const keyBytes = Buffer.from(key.padEnd(32).substring(0, 32), 'utf-8');
    
    // Encrypt
    const cipher = crypto.createCipheriv('aes-256-cbc', keyBytes, iv);
    const encrypted = Buffer.concat([
        cipher.update(data),
        cipher.final()
    ]);
    
    // Combine IV + ciphertext and return as Base64
    return Buffer.concat([iv, encrypted]).toString('base64');
}

export function decryptBytes(encryptedBase64: string): Buffer {
     const key = process.env.REACT_APP_ENCRYPTION_KEY || 'key';
    const encryptedBuffer = Buffer.from(encryptedBase64, 'base64');
    
    // Extract IV (first 16 bytes)
    const iv = encryptedBuffer.subarray(0, 16);
    const ciphertext = encryptedBuffer.subarray(16);
    
    // Prepare key (UTF-8, padded to 32 bytes)
    const keyBytes = Buffer.from(key.padEnd(32).substring(0, 32), 'utf-8');
    
    // Decrypt
    const decipher = crypto.createDecipheriv('aes-256-cbc', keyBytes, iv);
    const decrypted = Buffer.concat([
        decipher.update(ciphertext),
        decipher.final()
    ]);
    
    return decrypted;
}

export function combineAndDecryptPrivateKey(encryptedParts: string[]): string {
if(encryptedParts[0].length === 0 || encryptedParts[1].length === 0) {
    return '';
}

    if (encryptedParts.length !== 2) {
        throw new Error('Expected exactly 2 encrypted parts');
    }

    // Decrypt both parts
    const part1 = decryptBytes(encryptedParts[0]);
    const part2 = decryptBytes(encryptedParts[1]);

    // Combine back into original 32-byte key
    const combined = Buffer.concat([part1, part2]);
    return combined.toString('base64');
}

export function combineAndDecryptWalletPrivateKey(encryptedParts: string[]): string {
if(encryptedParts[0].length === 0 || encryptedParts[1].length === 0||
    encryptedParts[2].length === 0 || encryptedParts[3].length === 0
) {
    return '';
}

    if (encryptedParts.length !== 4) {
        throw new Error('Expected exactly 4 encrypted parts');
    }

    // Decrypt both parts
    const part1 = decryptBytes(encryptedParts[0]);
    const part2 = decryptBytes(encryptedParts[1]);
    const part3 = decryptBytes(encryptedParts[2]);
    const part4 = decryptBytes(encryptedParts[3]);

   

    // Combine back into original 32-byte key
    const combined = Buffer.concat([part1, part2, part3, part4]);
    return combined.toString('base64');
}

export function combineAndDecryptEVMWalletPrivateKey(encryptedParts: string[]): string {
if(encryptedParts[0].length === 0 || encryptedParts[1].length === 0||
    encryptedParts[2].length === 0 || encryptedParts[3].length === 0
) {
    return '';
}

    if (encryptedParts.length !== 4) {
        throw new Error('Expected exactly 4 encrypted parts');
    }

    // Decrypt both parts
    const part1 = decryptString(encryptedParts[0]);
    const part2 = decryptString(encryptedParts[1]);
    const part3 = decryptString(encryptedParts[2]);
    const part4 = decryptString(encryptedParts[3]);

   

    // Combine back into original 32-byte key
    const combined = `${part1}${part2}${part3}${part4}`;
    return combined;
}